import { Box } from '@material-ui/core';
import React, { ReactNode } from 'react';
import { getId } from 'globals/utils';
import FsBody from 'ui-elements/typography/FsBody';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import FsLink from 'ui-elements/links/FsLink';

export enum TooltipContentSingleItemType {
  TEXT = 'text',
  HEADLINE = 'headline',
  IMAGE = 'image',
  VIDEO = 'video',
  LINKS = 'links',
}

export interface TooltipContentLinkDetailsType {
  label: string;
  url: string;
}

export interface TooltipContentVideoDetailsType {
  url: string;
  title: string;
}

export interface TooltipContentImageDetailsType {
  url: string;
  alt: string;
}

export interface TooltipContentText {
  type: TooltipContentSingleItemType.TEXT;
  content: string;
}

export interface TooltipContentHeadline {
  type: TooltipContentSingleItemType.HEADLINE;
  content: string;
}

export interface TooltipContentImage {
  type: TooltipContentSingleItemType.IMAGE;
  content: TooltipContentImageDetailsType;
}

export interface TooltipContentLink {
  type: TooltipContentSingleItemType.LINKS;
  content: TooltipContentLinkDetailsType[];
}

export interface TooltipContentVideo {
  type: TooltipContentSingleItemType.VIDEO;
  content: TooltipContentVideoDetailsType;
}

export type TooltipContentSingleItem =
  | TooltipContentText
  | TooltipContentHeadline
  | TooltipContentImage
  | TooltipContentLink
  | TooltipContentVideo;

export const getHelperTooltipContent = (contentItems: string[]): ReactNode => {
  return contentItems.map((paragraph) => (
    <Box marginBottom={2} key={getId()}>
      <FsBody>{paragraph}</FsBody>
    </Box>
  ));
};

export const getItemContent = ({
  type,
  content,
}: TooltipContentSingleItem): ReactNode => {
  switch (type) {
    case TooltipContentSingleItemType.TEXT:
      return <FsBody>{content}</FsBody>;
    case TooltipContentSingleItemType.HEADLINE:
      return <FsBodyBold>{content}</FsBodyBold>;
    case TooltipContentSingleItemType.IMAGE:
      return (
        <img
          src={(content as TooltipContentImageDetailsType).url}
          alt={(content as TooltipContentImageDetailsType).alt}
        />
      );
    case TooltipContentSingleItemType.LINKS:
      return (
        <Box>
          <FsBodyBold>Links:</FsBodyBold>
          <ul>
            {(content as TooltipContentLinkDetailsType[]).map(
              ({ label, url }) => (
                <li key={getId() + url}>
                  <FsLink to={url}>{label}</FsLink>
                </li>
              )
            )}
          </ul>
        </Box>
      );
    case TooltipContentSingleItemType.VIDEO:
      return (
        <iframe
          title={(content as TooltipContentVideoDetailsType).title}
          width="560"
          height="315"
          src={(content as TooltipContentVideoDetailsType).url}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    default:
      return <FsBody>{content}</FsBody>;
  }
};

const getHorizontalAlignment = (
  type: TooltipContentSingleItemType
): string | undefined => {
  return type === TooltipContentSingleItemType.IMAGE ||
    type === TooltipContentSingleItemType.VIDEO
    ? 'center'
    : undefined;
};

export const getTooltipContent = (
  contentItems: TooltipContentSingleItem[]
): ReactNode => {
  return contentItems.map((item) => (
    <Box
      key={getId()}
      marginBottom={
        item.type !== TooltipContentSingleItemType.HEADLINE ? 2 : undefined
      }
      display="flex"
      justifyContent={getHorizontalAlignment(item.type)}
    >
      {getItemContent(item)}
    </Box>
  ));
};
